import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { selectors } from "reducers";
import {
  getBonafideInsuranceInfo,
  submitBonafideOrder
} from "features/bonafide/actions";
import BonafideOrderStatus from "./bonafide-order-status";
import BonafideOrderInfo from "./bonafide-order-info";
import BonafideFooter from "./bonafide-footer";
import AuthForm from "./auth-form";
import { getPendingOrderTasksHoldingOrder } from "features/tasks/helpers";
import { OrderProfileContext } from "components/order-profile-new/context";
import { formatDate } from "utils/dates";
import { useFetch } from "hooks";
import { post } from "utils/api";
import { MdBugReport } from "react-icons/lib/md";
import PureModal from "components/ui/modal/pure-modal";
import HasRolePermission from "components/shared/has-role-permission";

const BonafidePanel = ({
  visible,
  getBonafideInsuranceInfo,
  submitBonafideOrder,
  orderId,
  verificationHistory
}) => {
  const {
    tasks,
    order,
    fetchOrderLineItemsApi,
    getPatientTasks,
    fetchOrderProfile
  } = React.useContext(OrderProfileContext);

  const patientId = order.patient_id;
  const orderLineItems = fetchOrderLineItemsApi.response;

  const hasPendingTask = getPendingOrderTasksHoldingOrder(tasks, orderId) > 0;
  const [isFetching, setIsFetching] = React.useState(false);
  const [editAuthScreen, setEditAuth] = React.useState(false);
  const [index, setIndex] = React.useState(0);
  const initData = async () => {
    await getBonafideInsuranceInfo(orderId);
  };

  React.useEffect(() => {
    initData();
    setIndex(0);
  }, [orderId]);

  const bonafideLastResult = verificationHistory?.[0] ?? {};
  const bonafideSelectedResult = verificationHistory?.[index] ?? {};

  let currentStep = 1;
  if (bonafideLastResult?.isConfirmed === true && bonafideLastResult?.response?.status != "failed" && bonafideLastResult?.response?.status != "error") {
    currentStep = 3;
  } else if (bonafideLastResult?.response) {
    currentStep = 2;
  }

  const { fetchData: checkCmnNeeded } = useFetch({
    apiFn: ({ orderId }) => post(`bonafide/check-cmn-needed`, { orderId })
  });

  return (
    <div className={`bonafide-panel ${visible ? "" : "hidden"}`}>
      <div
        className="bonafide-panel-header"
        style={{
          padding: 5,
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          gridGap: 10,
          alignItems: "center",
          borderBottom: "1px solid #ccc"
        }}
      >
        <label>History: </label>
        <select onChange={e => setIndex(e.target.value)} style={{ margin: 0 }}>
          {verificationHistory?.map((x, index) => (
            <>
              {index == 1 && (
                <option disabled key={index}>
                  ----------------------------------------------------------------
                </option>
              )}
              <option key={index + x.id} value={index}>
                {formatDate(x.requestedAt, "L hh:mm A")} -{" "}
                {x.isConfirmed ? "Order Submit" : "Order Verification"} -{" "}
                {x.response.status}{" "}
              </option>
            </>
          ))}
        </select>
      </div>
      {editAuthScreen ? (
        <AuthForm
          orderLineItems={orderLineItems}
          currentStep={currentStep}
          canOverrideOrderCreation={currentStep == 1}
          handleCancel={() => setEditAuth(false)}
          hasPendingTask={hasPendingTask}
          submitOrder={async () => {
            try {
              setEditAuth(false);
              if (isFetching) return;
              setIsFetching(true);
              const orderSummary = document.getElementById(
                `copyArea-${orderId}`
              );
              await submitBonafideOrder(
                orderId,
                orderSummary.innerText,
                true,
                true
              );
              fetchOrderProfile(orderId);
            } finally {
              setIsFetching(false);
            }
          }}
        />
      ) : (
        <div className="bonafide-result-container">
          <BonafideOrderStatus
            response={bonafideSelectedResult?.response}
            requestedAt={bonafideSelectedResult.requestedAt}
            requestedBy={bonafideSelectedResult.requestedBy}
          />
          <BonafideOrderInfo
            response={bonafideSelectedResult.response}
            responseBody={bonafideSelectedResult.responseBody}
          />
          <HasRolePermission allowedRoles={["Administrator"]}>
            <DebugWindow bonafideSelectedResult={bonafideSelectedResult} />
          </HasRolePermission>
        </div>
      )}
      <BonafideFooter
        isFetching={isFetching}
        hasPendingTask={hasPendingTask}
        canOverrideOrderCreation={currentStep == 1}
        currentStep={currentStep}
        handleRefresh={async () => {
          try {
            setIsFetching(true);
            await getBonafideInsuranceInfo(orderId);
          } finally {
            setIsFetching(false);
          }
        }}
        handleCreatePreOrder={async () => {
          try {
            if (isFetching) return;
            setIsFetching(true);

            const cmnTaskAdded = await checkCmnNeeded({ orderId });
            if (cmnTaskAdded) await getPatientTasks(patientId);

            const orderSummary = document.getElementById(`copyArea-${orderId}`);
            await submitBonafideOrder(orderId, orderSummary.innerText, false);
            fetchOrderProfile(orderId);
          } finally {
            setIsFetching(false);
          }
        }}
        handleCreateOrderWithAuth={() => {
          if (hasPendingTask) {
            alert(
              "Order has pending task, resolve hold before sending order for processing"
            );
            return;
          }
          setEditAuth(true);
        }}
        handleCreateOrder={async () => {
          if (hasPendingTask) {
            alert(
              "Order has pending task, resolve hold before sending order for processing"
            );
            return;
          } else {
            try {
              setEditAuth(false);
              if (isFetching) return;
              setIsFetching(true);
              const orderSummary = document.getElementById(
                `copyArea-${orderId}`
              );
              await submitBonafideOrder(
                orderId,
                orderSummary.innerText,
                true,
                false
              );
              fetchOrderProfile(orderId);
            } finally {
              setIsFetching(false);
            }
          }
        }}
        editAuthScreen={editAuthScreen}
      />
    </div>
  );
};

BonafidePanel.propTypes = {
  orderId: PropTypes.string.isRequired,
  getBonafideInsuranceInfo: PropTypes.func.isRequired,
  submitBonafideOrder: PropTypes.func.isRequired,
  canOverrideOrderCreation: PropTypes.bool,
  verificationHistory: PropTypes.arrayOf(PropTypes.object),
  visible: PropTypes.bool
};

const DebugWindow = ({ bonafideSelectedResult }) => {
  if (!bonafideSelectedResult) return null;
  const formatJson = (str) => {
    try { return JSON.stringify(JSON.parse(str), null, 2); }
    catch (e) { return str; }
  };
  return (
    <PureModal
      renderTrigger={({ openModal }) => (
        <div style={{ display: "flex", flex: 1, justifyContent: "flex-end", width: "100%" }}>
          <i title="Debug" className="refresh-button" onClick={openModal}>
            <MdBugReport />
          </i>
        </div>
      )}
    >
      <div style={{ padding: 10 }}>
        <h5>Request</h5>
        <pre>{formatJson(bonafideSelectedResult.requestBody)}</pre>
        <h5>Response</h5>
        <pre>{formatJson(bonafideSelectedResult.responseBody)}</pre>
      </div>
    </PureModal>
  );
};

export default connect(
  (state, { orderId }) => {
    return {
      verificationHistory: selectors.getBonafideOrderResult(state, { orderId })
    };
  },
  {
    getBonafideInsuranceInfo,
    submitBonafideOrder
  }
)(BonafidePanel);
